import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Save, Loader2, Mail, Info, Send, Megaphone } from 'lucide-react';
import { toast } from 'sonner';

export default function EmailSettingsPanel() {
  const [form, setForm] = useState({
    notification_from_email: '', support_email: '',
    tpl_hidden_intro: '', tpl_hidden_action: '',
    tpl_removed_intro: '', tpl_removed_action: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Mass email
  const [recipients, setRecipients] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get('/admin/email-settings')
      .then(r => setForm(r.data))
      .catch(() => toast.error('Could not load email settings'))
      .finally(() => setLoading(false));
    api.get('/admin/mass-email/recipients').then(r => setRecipients(r.data.recipients)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put('/admin/email-settings', form);
      setForm(r.data);
      toast.success('Email settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const sendMass = async () => {
    setSending(true);
    try {
      const r = await api.post('/admin/mass-email', { subject: subject.trim(), body: body.trim() });
      toast.success(`Email queued to ${r.data.recipients} ${r.data.recipients === 1 ? 'recipient' : 'recipients'}`);
      setSubject(''); setBody(''); setConfirmOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send');
    } finally {
      setSending(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const massValid = subject.trim().length > 0 && body.trim().length > 0;

  if (loading) return <div className="text-brand-slate text-sm" data-testid="email-settings-loading">Loading…</div>;

  return (
    <div data-testid="email-settings-panel">
      <h2 className="font-display font-bold text-2xl text-brand-ink mb-1 flex items-center gap-2">
        <Mail className="w-5 h-5 text-brand" /> Email settings
      </h2>
      <p className="text-brand-slate text-sm mb-6">Owner-only. Manage addresses, the automated email wording, and send announcements.</p>

      <div className="glass-panel rounded-3xl p-6 space-y-5 max-w-2xl">
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-brand-dim">Notification sender (noreply)</Label>
          <Input data-testid="email-notification-from" type="email" value={form.notification_from_email || ''} onChange={set('notification_from_email')} placeholder="noreply@curbalert.org" className="mt-2 rounded-xl" />
          <p className="mt-1.5 text-xs text-brand-dim">The "From" address on automated emails.</p>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-brand-dim">Support / contact email</Label>
          <Input data-testid="email-support" type="email" value={form.support_email || ''} onChange={set('support_email')} placeholder="support@curbalert.org" className="mt-2 rounded-xl" />
          <p className="mt-1.5 text-xs text-brand-dim">Shown to users on the Contact page.</p>
        </div>

        <div className="flex items-start gap-2 rounded-2xl bg-brand/5 p-3 text-xs text-brand-slate">
          <Info className="w-4 h-4 text-brand mt-0.5 shrink-0" />
          <span>The notification sender must be an address your Google Workspace account is allowed to send as (a verified alias or "Send mail as" address). Otherwise Google rewrites it.</span>
        </div>

        <div className="pt-4 border-t border-black/5" />
        <h3 className="font-display text-lg text-brand-ink">Automated email wording</h3>
        <p className="text-xs text-brand-slate -mt-2">Sent to a pin's owner. The CurbAlert header, title, and button stay the same.</p>

        <div className="rounded-2xl border border-black/5 p-4 space-y-3">
          <p className="text-sm font-semibold text-brand-ink">“Your pin was hidden”</p>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-brand-dim">Intro line</Label>
            <Textarea data-testid="tpl-hidden-intro" value={form.tpl_hidden_intro || ''} onChange={set('tpl_hidden_intro')} className="mt-2 rounded-xl min-h-[70px]" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-brand-dim">Action line</Label>
            <Textarea data-testid="tpl-hidden-action" value={form.tpl_hidden_action || ''} onChange={set('tpl_hidden_action')} className="mt-2 rounded-xl min-h-[70px]" />
          </div>
        </div>

        <div className="rounded-2xl border border-black/5 p-4 space-y-3">
          <p className="text-sm font-semibold text-brand-ink">“Your pin was removed”</p>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-brand-dim">Intro line</Label>
            <Textarea data-testid="tpl-removed-intro" value={form.tpl_removed_intro || ''} onChange={set('tpl_removed_intro')} className="mt-2 rounded-xl min-h-[70px]" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-brand-dim">Action line</Label>
            <Textarea data-testid="tpl-removed-action" value={form.tpl_removed_action || ''} onChange={set('tpl_removed_action')} className="mt-2 rounded-xl min-h-[70px]" />
          </div>
        </div>

        <div className="pt-2 flex justify-end">
          <Button data-testid="email-settings-save-btn" onClick={save} disabled={saving} className="rounded-full bg-brand hover:bg-brand-hover text-white px-6">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save changes
          </Button>
        </div>
      </div>

      {/* Mass email */}
      <h2 className="font-display font-bold text-2xl text-brand-ink mt-10 mb-1 flex items-center gap-2">
        <Megaphone className="w-5 h-5 text-brand" /> Send an announcement
      </h2>
      <p className="text-brand-slate text-sm mb-4">
        Emails all users who have notifications turned on{recipients != null && <> — currently <span className="font-semibold text-brand-ink" data-testid="mass-email-recipients">{recipients}</span> {recipients === 1 ? 'recipient' : 'recipients'}</>}.
      </p>

      <div className="glass-panel rounded-3xl p-6 space-y-4 max-w-2xl">
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-brand-dim">Subject</Label>
          <Input data-testid="mass-email-subject" value={subject} onChange={e => setSubject(e.target.value)} maxLength={150} placeholder="e.g. New neighborhood yard sale weekend!" className="mt-2 rounded-xl" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-brand-dim">Message</Label>
          <Textarea data-testid="mass-email-body" value={body} onChange={e => setBody(e.target.value)} maxLength={5000} placeholder="Write your announcement… line breaks are preserved." className="mt-2 rounded-xl min-h-[160px]" />
          <p className="mt-1 text-xs text-brand-dim text-right">{body.length}/5000</p>
        </div>
        <div className="flex justify-end">
          <Button data-testid="mass-email-send-btn" onClick={() => setConfirmOpen(true)} disabled={!massValid || !recipients} className="rounded-full bg-brand hover:bg-brand-hover text-white px-6">
            <Send className="w-4 h-4 mr-2" /> Review &amp; send
          </Button>
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={(o) => !o && setConfirmOpen(false)}>
        <DialogContent className="rounded-3xl" data-testid="mass-email-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Megaphone className="w-5 h-5 text-brand" /> Send to {recipients} {recipients === 1 ? 'person' : 'people'}?</DialogTitle>
            <DialogDescription>
              This emails everyone with notifications enabled. Please double-check your subject and message — it can't be unsent.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-2xl bg-black/5 p-4">
            <p className="text-sm font-semibold text-brand-ink mb-1">{subject || '(no subject)'}</p>
            <p className="text-sm text-brand-slate whitespace-pre-wrap line-clamp-6">{body}</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="rounded-full" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button data-testid="mass-email-confirm-btn" onClick={sendMass} disabled={sending} className="rounded-full bg-brand hover:bg-brand-hover text-white">
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />} Send now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
